import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { setTitle } from '../utils/environment';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [returnUrl, setReturnUrl] = useState(null); // Store return URL in state
  const [isRedirecting, setIsRedirecting] = useState(false); // Prevent re-renders during redirect

  const { login, isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse and store return URL on mount (before any navigation can lose it)
  useEffect(() => {
    // addDebug(`Login page loaded`);
    // addDebug(`Full URL: ${window.location.href}`);
    // addDebug(`Search: ${window.location.search}`);
    // addDebug(`Hash: ${window.location.hash}`);
    // addDebug(`isAuthenticated: ${isAuthenticated}`);

    // Parse return URL from hash query params
    // URL structure from oauth-callback.html: http://localhost:3000/#/login?return=...
    const hash = window.location.hash; // Gets "#/login?return=..."
    const queryStartIndex = hash.indexOf('?');

    let returnParam = null;
    if (queryStartIndex > -1) {
      const queryString = hash.substring(queryStartIndex + 1); // Get everything after ?
      const searchParams = new URLSearchParams(queryString);
      returnParam = searchParams.get('return');
    }

    if (returnParam) {
      setReturnUrl(returnParam);
      // addDebug(`✓ OAuth Return URL found: ${returnParam.substring(0, 80)}...`);
    } else {
      // Check location state for internal redirects
      const internalRedirect = location.state?.from?.pathname;
      if (internalRedirect) {
        setReturnUrl(internalRedirect);
        // addDebug(`Internal redirect to: ${internalRedirect}`);
      } else {
        setReturnUrl('/dashboard');
        // addDebug(`⚠ No return URL - using default: /dashboard`);
      }
    }
  }, []); // Only run once on mount

  // Redirect if already authenticated (only runs on page load, not after login)
  useEffect(() => {
    // Set page title with environment prefix
    setTitle('Login');

    // Only redirect if already authenticated when page loads
    // (Don't redirect here after login - handleSubmit handles that)
    if (isAuthenticated && returnUrl) {
      // addDebug(`Already authenticated on page load, redirecting...`);

      if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
        // addDebug(`⏩ External OAuth redirect...`);
        window.location.replace(returnUrl);
      } else {
        navigate(returnUrl, { replace: true });
      }
    }
  }, []); // Only run on mount, not when isAuthenticated changes
  
  // Clear error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // addDebug(`🔐 Logging in...`);

    try {
      const result = await login(formData);
      // addDebug(`Login result: ${result.success ? '✓ SUCCESS' : '✗ FAILED'}`);

      if (result.success) {
        // addDebug(`Redirect target: ${returnUrl ? returnUrl.substring(0, 80) : 'null'}...`);

        if (!returnUrl) {
          // addDebug(`✗ ERROR: No return URL stored!`);
          return;
        }

        // If return URL is external (OAuth), redirect to it directly
        if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
          // addDebug(`⏩ OAuth redirect - performing immediately...`);
          console.log('[OAuth] Full redirect URL:', returnUrl);

          // Set flag to prevent any re-renders
          setIsRedirecting(true);

          // Perform immediate redirect without setTimeout
          window.location.replace(returnUrl);

          // Should never reach here, but just in case
          return;
        } else {
          // Internal navigation
          // addDebug(`Internal navigation to: ${returnUrl}`);
          navigate(returnUrl, { replace: true });
        }
      } else {
        // addDebug(`✗ Login failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      // addDebug(`✗ Error: ${error.message}`);
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render anything if we're redirecting
  if (isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to authorization...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-16 w-16 flex items-center justify-center">
            <img src={new URL('../assets/logo.png', import.meta.url).href} alt="Read It Never" className="h-16 w-16 rounded-2xl" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Sign in to Read It Never
          </h2>
          <p className="mt-2 text-center text-lg font-medium text-gray-700">
            Reading is slow. Just vibe it.
          </p>
          <p className="mt-4 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              create a new account
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field mt-1"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="input-field mt-1"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting && (
                <div className="spinner w-4 h-4 mr-2"></div>
              )}
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">
              By signing in, you agree to our{' '}
              <a href="/#/terms" className="text-primary-600 hover:text-primary-500">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/#/privacy" className="text-primary-600 hover:text-primary-500">
                Privacy Policy
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;